import React from 'react';
import {
  Button, Col, Form, Row,
} from 'react-bootstrap';
import {
  ChoiceMemoryContext,
  ChoiceMemoryProvider,
} from '../../../../contexts/ChoiceMemory/ChoiceMemoryContext';
import { Preview } from '../../../ui/Preview/Preview';
import './Preview.scss';
import { isAudiosEnabled } from '../../../utils/userActionUtils';
import { replaySounds } from '../../../ui/Preview/useChoiceMemoryCallbacks/useChoiceMemoryCallbacks';
import { stopAllAudio, muteAudio } from "../../../pages/AudioStepSettingsModal/AudioPlay/AudioController";
import { IoVolumeHighOutline, IoVolumeMuteOutline, IoStopOutline, IoPlayOutline } from "react-icons/io5";
import { FEATURE_NODE_EDIT_SIDE_PANEL } from '../../../../helpers/featureFlags';

/**
 * DetachablePreview component
 * @param {boolean} show - show/hide detachable preview
 * @param {string} storyUuid - story uuid
 * @param {string} bookStyle - book style
 * @param {object} store - store object
 * @param {function} onClose - function to close detachable preview
 * @param {function} onPreviewExit - function to exit preview
 * @param {boolean} isBranchDetailsCacheDisabled - flag to disable branch details cache
 * @param {boolean} hideDetach - flag to hide detach button
 * @param {boolean} showAnalyticsPanel - flag to show analytics panel
 * @param {string} displayAnalyticsOption - display analytics option
 * @param {string} bookId - book id
 * @param {function} update - function to update detachable preview
 * @param {function} scrollToBranch - function to scroll to branch
 * @param {Array} branches - client-side branch list passed from index.js (state.branch[])
 * @param {Array} answerTypes - answer types from index.js state.answerTypes
 * @param {Array} modifierTypes - modifier types from index.js state.modifierTypes
 */
export class DetachablePreview extends React.PureComponent {
  constructor(props) {
    super(props);
    const { displayAnalyticsOption } = props;
    this.externalWindow = null;
    this.state = {
      portal: false,
      displayAnalyticsOption,
      branchId: null,
      soundOn: true,
    };
  }

  closeWindow = () => {
    this.setState({ portal: false });
    if (this.externalWindow) this.externalWindow.close();
    this.externalWindow = null;
  };

  onExternalClose = () => {
    const { portal } = this.state;

    if (portal && this.externalWindow) {
      this.setState({ portal: false });
      this.externalWindow = null;
    }
  };

  openWindow = () => {
    const { storyUuid } = this.props;

    stopAllAudio();
    this.externalWindow = window.open(`/play/${storyUuid}`, '_blank');

    this.externalWindow.document.title = document.title;

    // NOTE: Different browser and also different versions of same browser
    // have strange behaviour for this events.
    // Dirty fix.
    this.externalWindow.addEventListener('beforeunload', this.onExternalClose, { capture: true });
    this.externalWindow.addEventListener('close', this.onExternalClose, { capture: true });
    this.externalWindow.addEventListener('unload', this.onExternalClose, { capture: true });

    this.externalWindow.addEventListener('load', () => {
      const { portal } = this.state;

      if (!portal) {
        this.setState({ portal: true });
      }
    });
  };

  beforeUnload = () => {
    this.closeWindow();
  };

  async componentDidMount() {
    const { store, displayAnalyticsOption, bookId } = this.props;
    if (store) {
      store.setDisplayAnalytics(displayAnalyticsOption);
    }
    if (bookId) {
      const choiceMemory = this.context;
      await choiceMemory.fetchVariables(bookId);
    }
  }

  async componentDidUpdate(prevProps, prevState) {
    const {
      update, show, bookId, store, displayAnalyticsOption: displayAnalyticsOptionProps, reFetchMemoriesTrigger,
    } = this.props;

    const { portal, displayAnalyticsOption: displayAnalyticsOptionState } = this.state;

    if (prevState.portal !== portal) {
      update(!!(show && !portal));
    }

    if (prevProps.show !== show) {
      this.closeWindow();
      update(!!(show && !portal));
      if (show) {
        window.addEventListener('beforeunload', this.beforeUnload, { passive: true });
        if (bookId) {
          const choiceMemory = this.context;
          choiceMemory.fetchVariables(bookId);
        }
      } else {
        window.removeEventListener('beforeunload', this.beforeUnload);
      }
    }

    if (displayAnalyticsOptionState !== prevState.displayAnalyticsOption) {
      if (store) {
        store.setDisplayAnalytics(displayAnalyticsOptionState);
      }
    }

    if (displayAnalyticsOptionProps !== prevProps.displayAnalyticsOption) {
      this.setState({ displayAnalyticsOption: displayAnalyticsOptionProps });
    }

    if (prevProps.bookId !== bookId) {
      const choiceMemory = this.context;
      await choiceMemory.fetchVariables(bookId);
    }

    // TODO: Remove this after refactoring
    if (prevProps.reFetchMemoriesTrigger !== reFetchMemoriesTrigger) {
      const choiceMemory = this.context;
      await choiceMemory.fetchVariables(bookId);
    }
  }

  componentWillUnmount() {
    stopAllAudio();
    window.removeEventListener('beforeunload', this.beforeUnload);
    this.closeWindow();
  }

  handleShowNodeClick = () => {
    const { scrollToBranch, cursorBranchId } = this.props;
    // In controlled mode cursor is the single source of position (DEV-RULES §4).
    // state.branchId is not updated by emulator gestures in this mode.
    const targetBranchId = cursorBranchId ?? this.state.branchId;

    if (targetBranchId) {
      scrollToBranch(targetBranchId, true);
    }
  };

  handleEditNodeClick = () => {
    const { store, onEditStep, cursorBranchId } = this.props;
    const currentStepId = store?.currentStep?.id;
    // In controlled mode store.currentBranchId lags behind cursor — CT navigation is vetoed
    // so the store never advances past the last node the author explicitly opened.
    const currentBranchId = cursorBranchId ?? store?.currentBranchId;
    onEditStep?.(currentBranchId, currentStepId);
  };

  toggleSound = () => {
    this.setState((prevState) => {
      const newSoundOn = !prevState.soundOn;
      muteAudio(!newSoundOn);
      if (!newSoundOn) {
        stopAllAudio();
      }
      return { soundOn: newSoundOn };
    });
    
  };

  replaySound = () => {
    replaySounds();
    
  };

  renderAudioControls() {
    const { soundOn } = this.state;
    if (!isAudiosEnabled()) return null;
    return (
      <div className="p-2 mt-1 d-inline-block text-center w-100 d-flex justify-content-center audioContainer">
        <Row className="mt-2 d-flex justify-content-center">
          <Col xs="auto" className="d-flex align-items-center">
            <span className="font-weight-bold">Audio:</span>
          </Col>
          <Col md="auto">
            <Button
              onClick={this.toggleSound}
              variant="outline-primary"
              size="sm"
              aria-label={soundOn ? "Mute sound" : "Unmute sound"}
            >
              {soundOn ? <IoVolumeHighOutline size={20} /> : <IoVolumeMuteOutline size={20} />}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              onClick={stopAllAudio}
              variant="outline-primary"
              size="sm"
              aria-label="Stop sound"
              disabled={!soundOn}
              style={!soundOn ? { opacity: 0.25, cursor: "not-allowed" } : {}}
            >
              <IoStopOutline size={20} />
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              onClick={this.replaySound}
              variant="outline-primary"
              size="sm"
              aria-label="Replay sound"
              disabled={!soundOn}
              style={!soundOn ? { opacity: 0.25, cursor: "not-allowed" } : {}}
            >
              <IoPlayOutline size={20} />
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  renderPanel() {
    const { hideDetach, onClose, addBranchActive } = this.props;

    // In edit mode the buttons are invisible but the container stays in the DOM so
    // the emulator does not jump when a node is opened or closed (height reserved).
    const isHidden = FEATURE_NODE_EDIT_SIDE_PANEL && addBranchActive;

    return (
      <div className="navi" style={isHidden ? { visibility: 'hidden' } : undefined}>
        {/* Detach/Close are hidden in side-by-side mode: the form owns layout and
            closing, and detaching the emulator has no meaning there. */}
        {!FEATURE_NODE_EDIT_SIDE_PANEL && (
        <Row className="d-flex justify-content-center">
          {!hideDetach
          && (
          <Col>
            <Button
              block
              onClick={this.openWindow}
              variant="primary"
              size="sm"
            >
              Detach
            </Button>
          </Col>
          )}
          <Col>
            <Button
              disabled={!onClose}
              block
              onClick={onClose}
              variant="secondary"
              size="sm"
            >
              Close
            </Button>
          </Col>
        </Row>
        )}
        <Row className="mt-2 mr-2 d-flex justify-content-center">
          <Col sm="auto">
            <Button
              onClick={this.handleShowNodeClick}
              variant="outline-primary"
              size="sm"
            >
              Show this node
            </Button>
          </Col>
          <Col md="auto">
            <Button
              onClick={this.handleEditNodeClick}
              variant="outline-primary"
              size="sm"
            >
              Edit this node
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  handleAnalyticOptionChanged(event) {
    this.setState({ displayAnalyticsOption: event.target.value });
  }

  handleScrollToBranch = (branchId) => {
    const { scrollToBranch } = this.props;
    this.setState({ branchId });
    scrollToBranch(branchId);
  };

  render() {
    const {
      show,
      storyUuid,
      bookStyle,
      store,
      onPreviewExit,
      isBranchDetailsCacheDisabled,
      showAnalyticsPanel,
      bookId,
      episodeId,
      branches,
      answerTypes,
      modifierTypes,
    } = this.props;

    const {
      portal,
      displayAnalyticsOption,
    } = this.state;

    if (portal || !storyUuid) {
      return (
        <div className="previewDock" />
      );
    }

    return (
      <div
        className="previewDock"
      >
        {this.renderPanel()}
        {this.renderAudioControls()}
        { showAnalyticsPanel && (
        <div className="analyticsPanelInPreview">
          <div className="analyticsPanelInPreviewTitle">
            Performance:
          </div>
          <Form.Check
            custom
            type="radio"
            inline
            label="Hide"
            id="Hide"
            value="hide"
            checked={displayAnalyticsOption === 'hide'}
            onChange={(event) => this.handleAnalyticOptionChanged(event)}
          />
          <Form.Check
            custom
            type="radio"
            inline
            label="as count"
            value="count"
            checked={displayAnalyticsOption === 'count'}
            onChange={(event) => this.handleAnalyticOptionChanged(event)}
            id="count"
          />
          <Form.Check
            custom
            type="radio"
            inline
            label="as percent"
            value="percentage"
            checked={displayAnalyticsOption === 'percentage'}
            onChange={(event) => this.handleAnalyticOptionChanged(event)}
            id="percentage"
          />
        </div>
        )}
        <ChoiceMemoryProvider>
          {storyUuid && (
            <Preview
              bookId={bookId}
              episodeId={FEATURE_NODE_EDIT_SIDE_PANEL ? episodeId : undefined}
              episodeUuid={storyUuid}
              bookStyle={bookStyle}
              // TODO: make width flexible on GR side.
              // 75% of width of previewDock which is 30em, where font-size is 16px
              width={345}
              store={store}
              onPreviewExit={onPreviewExit}
              isBranchDetailsCacheDisabled={isBranchDetailsCacheDisabled}
              scrollToBranch={this.handleScrollToBranch}
              branches={branches}
              answerTypes={answerTypes}
              modifierTypes={modifierTypes}
              bookLocations={this.props.bookLocations}
            />
          )}
        </ChoiceMemoryProvider>
      </div>
    );
  }
}

DetachablePreview.contextType = ChoiceMemoryContext;
